
import React, { useState, useEffect } from 'react';
import { Copy, X, Share2, Check, Download, FileCode } from 'lucide-react';
import { ProjectState } from '../types';
import { exportProjectToJSON, exportProjectToMSP } from '../services/ioService';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  text: string;
  project: ProjectState;
}

const ShareDialog: React.FC<ShareDialogProps> = ({ isOpen, onClose, text, project }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) setCopied(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-slate-800">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-indigo-400" />
            Share Schedule Sequence
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-hidden flex flex-col gap-6">
          
          {/* Export Options */}
          <div className="grid grid-cols-2 gap-4">
             <button 
                onClick={() => exportProjectToJSON(project)}
                className="flex items-center justify-center gap-3 p-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-all group"
             >
                <div className="p-2 bg-slate-950 rounded-lg text-slate-400 group-hover:text-white transition-colors">
                    <Download className="w-5 h-5" />
                </div>
                <div className="text-left">
                    <div className="text-sm font-semibold text-white">Export JSON</div>
                    <div className="text-[10px] text-slate-500">Raw project data file</div>
                </div>
             </button>

             <button 
                onClick={() => exportProjectToMSP(project)}
                className="flex items-center justify-center gap-3 p-4 bg-indigo-900/20 hover:bg-indigo-900/30 border border-indigo-500/30 rounded-xl transition-all group"
             >
                <div className="p-2 bg-indigo-950 rounded-lg text-indigo-400 group-hover:text-indigo-300 transition-colors">
                    <FileCode className="w-5 h-5" />
                </div>
                <div className="text-left">
                    <div className="text-sm font-semibold text-indigo-300">Export MS Project</div>
                    <div className="text-[10px] text-indigo-500/70">Compatible XML format</div>
                </div>
             </button>
          </div>

          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex justify-between items-end mb-3">
                <div>
                    <h3 className="text-sm font-semibold text-slate-300">Schedule Sequence</h3>
                    <p className="text-[10px] text-slate-500">Copy text format for documentation or chat</p>
                </div>
                <button
                    onClick={handleCopy}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all shadow-lg ${
                        copied 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    }`}
                >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied!' : 'Copy Text'}
                </button>
            </div>
            
            <div className="flex-1 relative bg-slate-950 rounded-lg border border-slate-800 overflow-hidden">
                <textarea 
                    className="w-full h-full p-4 bg-transparent text-xs font-mono text-slate-300 resize-none focus:outline-none"
                    value={text}
                    readOnly
                />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ShareDialog;
